import React from "react";
import { Button, useToast } from "@chakra-ui/react";

const waitForPiInit = () => {
  return new Promise((resolve, reject) => {
    const maxAttempts = 50;
    let attempts = 0;

    const check = () => {
      if (window.Pi && window.piAutoInitDone) {
        console.log("✅ Pi SDK fully initialized.");
        resolve();
      } else if (attempts >= maxAttempts) {
        reject(new Error("❌ Pi SDK failed to initialize."));
      } else {
        attempts++;
        setTimeout(check, 100);
      }
    };

    check();
  });
};

const TipButton = () => {
  const toast = useToast();

  const handleTip = async () => {
    if (!window.Pi) {
      toast({
        title: "Not in Pi Browser",
        description: "Please open this app inside the Pi Browser.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      await waitForPiInit();

      const onReadyForServerApproval = async (paymentId) => {
        console.log("📩 Approving paymentId:", paymentId);
        try {
          const res = await fetch("https://pi-chart-backend.onrender.com/payment/approve", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ paymentId }),
          });

          if (!res.ok) {
            const errorText = await res.text();
            console.error("❌ Approval failed:", errorText);
            throw new Error("Approval failed");
          }

          console.log("✅ Payment approved successfully:", paymentId);
        } catch (error) {
          console.error("❌ Error in approval:", error);
          toast({
            title: "Approval Error",
            description: error.message || "An error occurred while processing the payment.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      };

      const onReadyForServerCompletion = async (paymentId, txid) => {
        console.log("✅ Completing paymentId:", paymentId, "with txid:", txid);

        try {
          const res = await fetch("https://pi-chart-backend.onrender.com/payment/complete", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ paymentId, txid }),
          });

          if (!res.ok) {
            const errorText = await res.text();
            throw new Error(errorText);
          }

          toast({
            title: "Thanks for the tip!",
            description: "You're awesome 💜",
            status: "success",
            duration: 3000,
            isClosable: true,
          });

          console.log("✅ Payment completed successfully:", paymentId);
        } catch (error) {
          console.error("❌ Error in completion:", error);
          toast({
            title: "Completion Error",
            description: error.message || "An error occurred while completing the payment.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      };

      const onCancel = (paymentId) => {
        console.warn("❌ Payment cancelled:", paymentId);
        toast({
          title: "Tip cancelled",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
      };

      const onError = (error, payment) => {
        console.error("❌ Payment error:", error, payment);
        toast({
          title: "Payment Error",
          description: error.message || "Something went wrong.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      };

      const onIncompletePaymentFound = (payment) => {
        console.warn("🟡 Incomplete payment found:", payment);
        toast({
          title: "Unfinished Payment Detected",
          description: "A previous payment needs resolution. Please try again.",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
      };

      await window.Pi.createPayment(
        {
          amount: 1,
          memo: "Support PiChart 💡",
          metadata: { type: "tip", project: "PiChart" },
          to: "Mejm87",
        },
        {
          onReadyForServerApproval,
          onReadyForServerCompletion,
          onCancel,
          onError,
          onIncompletePaymentFound, // ✅ make sure this is in-scope
        }
      );
    } catch (err) {
      console.error("❌ Tip failed:", err);
      toast({
        title: "Tip Failed",
        description: err.message || "Something went wrong.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Button onClick={handleTip} colorScheme="green" mt={8}>
      Tip 1π
    </Button>
  );
};

export default TipButton;
